/**
 * Mirroring for the swept brush: reflect *spans*, not primitives.
 *
 * `forEachReflection` in src/events/layerSystem.ts reflects one already-written cell across the
 * active mirror planes (edge planes at e + 0.5, integer form x' = 2e + 1 - x), enumerating the
 * non-empty subsets of active axes in the order X, Y, Z, XY, XZ, YZ, XYZ, and skipping reflections
 * that land out of bounds. This module does the same thing for a whole row span:
 *
 *   1. the original span is clipped to bounds (a cell that is not in the document is never written,
 *      so it must not produce a reflection either — this is why primitives are not reflected);
 *   2. each reflection of the clipped span is computed in the same mask order;
 *   3. each reflected span is clipped to bounds again.
 *
 * The result equals per-cell `forEachReflection` exactly, including off-centre planes and spans cut
 * by the document edge. Faces are not flipped here: the swept path never carries faced voxels.
 */

export type MirrorSpec = { x?: number; y?: number; z?: number }
export type Bounds = { x: number; y: number; z: number }

export type ReflectedSpanEmit = (
  y: number, z: number, x0: number, x1: number,
  maskX: boolean, maskY: boolean, maskZ: boolean,
) => void

export type MirrorPlan = {
  readonly masks: ReadonlyArray<readonly [boolean, boolean, boolean]>
  readonly twoExPlus1: number
  readonly twoEyPlus1: number
  readonly twoEzPlus1: number
}

export function hasMirrors(m: MirrorSpec | null | undefined): boolean {
  if (!m) return false
  return Number.isFinite(m.x as number) || Number.isFinite(m.y as number) || Number.isFinite(m.z as number)
}

/** Precompute the reflection masks (same order as layerSystem.forEachReflection). */
export function planMirrors(mirrors: MirrorSpec | null | undefined): MirrorPlan | null {
  if (!mirrors) return null
  const hasX = Number.isFinite(mirrors.x as number)
  const hasY = Number.isFinite(mirrors.y as number)
  const hasZ = Number.isFinite(mirrors.z as number)
  if (!hasX && !hasY && !hasZ) return null
  const masks: Array<readonly [boolean, boolean, boolean]> = []
  if (hasX) masks.push([true, false, false])
  if (hasY) masks.push([false, true, false])
  if (hasZ) masks.push([false, false, true])
  if (hasX && hasY) masks.push([true, true, false])
  if (hasX && hasZ) masks.push([true, false, true])
  if (hasY && hasZ) masks.push([false, true, true])
  if (hasX && hasY && hasZ) masks.push([true, true, true])
  return {
    masks,
    twoExPlus1: hasX ? (mirrors.x as number) * 2 + 1 : 0,
    twoEyPlus1: hasY ? (mirrors.y as number) * 2 + 1 : 0,
    twoEzPlus1: hasZ ? (mirrors.z as number) * 2 + 1 : 0,
  }
}

/** Clip a row span to the document; null when nothing remains. */
export function clipSpan(y: number, z: number, x0: number, x1: number, bounds: Bounds): [number, number] | null {
  if (y < 0 || z < 0 || y >= bounds.y || z >= bounds.z) return null
  if (x0 < 0) x0 = 0
  if (x1 > bounds.x - 1) x1 = bounds.x - 1
  return x0 <= x1 ? [x0, x1] : null
}

/**
 * Emit the reflections of span (y, z, [x0, x1]) across `mirrors`, clipped to `bounds`.
 * The original span is clipped first and is NOT emitted (callers write it themselves, exactly like
 * `writeFn` writes the base cell before calling `forEachReflection`).
 */
export function forEachReflectedSpan(
  y: number, z: number, x0: number, x1: number,
  mirrors: MirrorSpec | MirrorPlan | null | undefined,
  bounds: Bounds,
  emit: ReflectedSpanEmit,
): void {
  const plan = isPlan(mirrors) ? mirrors : planMirrors(mirrors)
  if (!plan) return
  const clipped = clipSpan(y, z, x0, x1, bounds)
  if (!clipped) return
  forEachReflectedSpanPlanned(plan, y, z, clipped[0], clipped[1], bounds, emit)
}

/** Same as forEachReflectedSpan, for a span that is already clipped to bounds. */
export function forEachReflectedSpanPlanned(
  plan: MirrorPlan,
  y: number, z: number, x0: number, x1: number,
  bounds: Bounds,
  emit: ReflectedSpanEmit,
): void {
  const { masks, twoExPlus1, twoEyPlus1, twoEzPlus1 } = plan
  for (let i = 0; i < masks.length; i++) {
    const m = masks[i]
    const mx = m[0], my = m[1], mz = m[2]
    let ny = y, nz = z
    if (my) ny = twoEyPlus1 - y
    if (mz) nz = twoEzPlus1 - z
    if (ny < 0 || nz < 0 || ny >= bounds.y || nz >= bounds.z) continue
    let nx0 = x0, nx1 = x1
    if (mx) { nx0 = twoExPlus1 - x1; nx1 = twoExPlus1 - x0 }
    if (nx0 < 0) nx0 = 0
    if (nx1 > bounds.x - 1) nx1 = bounds.x - 1
    if (nx0 > nx1) continue
    emit(ny, nz, nx0, nx1, mx, my, mz)
  }
}

function isPlan(m: MirrorSpec | MirrorPlan | null | undefined): m is MirrorPlan {
  return !!m && Array.isArray((m as MirrorPlan).masks)
}
